import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const OPEN = 7;
const CLOSE = 24;
const HOUR_H = 40;
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};
const startOfWeek = (d) => {
    const x = startOfDay(d);
    x.setDate(x.getDate() - x.getDay());
    return x;
};
const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hourLabel = (h) => (h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`);

const WeekPlanner = ({ refreshKey = 0 }) => {
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
    const [bookings, setBookings] = useState([]);
    const [blocks, setBlocks] = useState([]);

    useEffect(() => {
        api.get("/studio/bookings")
            .then((r) => setBookings(r.data))
            .catch(() => {});
        api.get("/studio/blocks/public")
            .then((r) => setBlocks(r.data))
            .catch(() => {});
    }, [refreshKey]);

    const now = startOfDay(new Date());
    const maxDay = addDays(now, 14);
    const canPrev = weekStart > startOfWeek(now);
    const canNext = addDays(weekStart, 7) <= maxDay;
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = Array.from({ length: CLOSE - OPEN }, (_, i) => OPEN + i);
    const bodyH = (CLOSE - OPEN) * HOUR_H;

    const fmtDay = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const rangeLabel = `${fmtDay(days[0])} – ${fmtDay(days[6])}, ${days[6].getFullYear()}`;

    return (
        <div data-testid="week-planner" className="border-2 border-ink bg-cream hard-shadow-lg">
            <div className="flex items-center justify-between border-b-2 border-ink bg-mustard px-4 py-3">
                <button
                    data-testid="week-prev"
                    onClick={() => canPrev && setWeekStart(addDays(weekStart, -7))}
                    disabled={!canPrev}
                    aria-label="Previous week"
                    className="border-2 border-ink bg-cream p-1.5 hard-shadow transition-transform duration-150 enabled:hover:-translate-y-0.5 disabled:opacity-30"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <p data-testid="week-label" className="font-display text-xl tracking-[0.12em] text-ink sm:text-2xl">
                    {rangeLabel.toUpperCase()}
                </p>
                <button
                    data-testid="week-next"
                    onClick={() => canNext && setWeekStart(addDays(weekStart, 7))}
                    disabled={!canNext}
                    aria-label="Next week"
                    className="border-2 border-ink bg-cream p-1.5 hard-shadow transition-transform duration-150 enabled:hover:-translate-y-0.5 disabled:opacity-30"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>
            <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                    <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b-2 border-ink bg-paper">
                        <div className="px-1 py-2" />
                        {days.map((d) => (
                            <div
                                key={iso(d)}
                                className={`border-l border-ink/15 px-1 py-2 text-center ${
                                    iso(d) === iso(now) ? "bg-mustard/40" : ""
                                } ${d > maxDay ? "opacity-40" : ""}`}
                            >
                                <p className="font-display text-[10px] tracking-[0.15em] text-ink/60">{DOW[d.getDay()]}</p>
                                <p className={`font-display text-base ${iso(d) === iso(now) ? "text-brick" : "text-ink"}`}>{d.getDate()}</p>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-[56px_repeat(7,1fr)]">
                        <div className="relative" style={{ height: bodyH }}>
                            {hours.map((h) => (
                                <p key={h} className="absolute right-1 -translate-y-1/2 font-display text-[10px] tracking-wide text-ink/50" style={{ top: (h - OPEN) * HOUR_H }}>
                                    {hourLabel(h)}
                                </p>
                            ))}
                        </div>
                        {days.map((d) => {
                            const dayIso = iso(d);
                            const todays = bookings.filter((b) => b.date === dayIso);
                            return (
                                <div
                                    key={dayIso}
                                    data-testid={`week-day-${dayIso}`}
                                    className={`relative border-l border-ink/15 ${iso(d) === iso(now) ? "bg-mustard/15" : ""} ${d > maxDay ? "bg-paper/60" : ""}`}
                                    style={{
                                        height: bodyH,
                                        backgroundImage: "repeating-linear-gradient(to bottom, rgba(13,38,31,0.12) 0, rgba(13,38,31,0.12) 1px, transparent 1px, transparent 40px)",
                                    }}
                                >
                                    {todays.map((b, j) => {
                                        const startH = parseInt(b.start.split(":")[0], 10);
                                        const overlap = todays.filter((o) => {
                                            const oStart = parseInt(o.start.split(":")[0], 10);
                                            return startH < oStart + o.hours && oStart < startH + b.hours;
                                        }).length;
                                        return (
                                            <div
                                                key={j}
                                                data-testid={`week-block-${dayIso}-${b.start}`}
                                                className="absolute inset-x-1 border-2 border-ink bg-brick px-1.5 py-0.5 text-cream"
                                                style={{ top: (startH - OPEN) * HOUR_H + 1, height: b.hours * HOUR_H - 3 }}
                                                title={`Reserved ${b.start} for ${b.hours} hr${b.hours > 1 ? "s" : ""}`}
                                            >
                                                <p className="font-display text-[10px] tracking-[0.12em]">RESERVED — WHOLE ROOM</p>
                                                {b.hours >= 2 && <p className="text-[10px] opacity-80">{hourLabel(startH)} – {hourLabel(startH + b.hours)}</p>}
                                            </div>
                                        );
                                    })}
                                    {blocks.filter((b) => b.date === dayIso).map((b, j) => {
                                        const startH = parseInt(b.start.split(":")[0], 10);
                                        return (
                                            <div
                                                key={`blk-${j}`}
                                                data-testid={`week-closed-${dayIso}-${b.start}`}
                                                className="absolute inset-x-1 border-2 border-dashed border-ink/50 bg-ink/25 px-1.5 py-0.5 text-ink/70"
                                                style={{ top: (startH - OPEN) * HOUR_H + 1, height: Math.min(b.hours, CLOSE - startH) * HOUR_H - 3 }}
                                                title="Closed — maintenance or shenanigans"
                                            >
                                                <p className="font-display text-[10px] tracking-[0.12em]">CLOSED</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <p className="border-t-2 border-ink bg-paper px-4 py-2.5 text-xs text-ink/60">
                Open 7 AM – midnight · brick blocks are spoken for · reservations open 14 days out
            </p>
        </div>
    );
};

export default WeekPlanner;
